import { map } from "nanostores";
import type { AbstractMesh, Scene } from "@babylonjs/core";
import { create } from "@bufbuild/protobuf";
import { ClientInputSchema, type Entity, JoinPlotSchema, LeavePlotSchema, type ServerMessage } from "./gen/livesoup/v1/realtime_pb";
import { $roomPhase } from "./room/phase";
import { client, PROTOCOL_VERSION } from "./net/codec";
import { WsClient } from "./net/ws";
import { $connection, $drafts, $leaveRequest, $panel, $playerPos, $plots, $presence, $remoteCount, $session, toast } from "./state/stores";
import { handlePlotsChanged } from "./state/plotsChanged";
import { loadMarkers } from "./state/markers";
import { onChatDeleted, onChatError, onChatHistory, onChatMessage } from "./state/chat";
import { onMailChanged } from "./state/mail";
import { onWelcome } from "./state/onWelcome";
import { describeError } from "./ui/errors";
import { draftsAfter, draftsSeen, dropDrafts, presenceFromProto } from "./world/plots";
import { zoneOf } from "./world/config";
import { plotLine, plotLocal } from "./world/plotCoords";
import { EntityManager } from "./engine/entities";
import { LocalPlayer, lobbySpace } from "./engine/player";
import { RemotePlayers } from "./engine/remote";
import type { BuiltWorld } from "./engine/worldBuild";
import type { HUD } from "./hud/hud";
import { refreshMyAssets } from "./state/myAssets";

export const $entities = map<Record<string, Entity>>({});

export interface WorldSession {
  ws: WsClient;
  player: LocalPlayer;
  remotes: RemotePlayers;
  entities: EntityManager;
  myId: string | null;
  stop(): void;
  toggleJoin(): void;
  /** Runs cb after every Welcome, the first and each reconnect (the room re-enters, spec 2o §4.1). */
  whenWelcomed(cb: () => void): void;
}

export function startWorldSession(scene: Scene, canvas: HTMLCanvasElement, world: BuiltWorld, hud: HUD): WorldSession {
  let lastPosAt = 0;
  const proto = location.protocol === "https:" ? "wss" : "ws";
  const ws = new WsClient(`${proto}://${location.host}/ws`);
  const player = new LocalPlayer(scene, canvas, lobbySpace(world));
  const shadowHook = (ms: AbstractMesh[], added: boolean): void =>
    ms.forEach((m) => (added ? world.atmosphere.addShadowCaster(m) : world.atmosphere.removeShadowCaster(m)));
  const remotes = new RemotePlayers(scene, shadowHook);
  const entities = new EntityManager(scene, shadowHook, () => player.feetPosition());
  $entities.subscribe((all) => entities.sync(all, new Set(Object.keys($drafts.get()))));
  $drafts.subscribe((d) => entities.sync($entities.get(), new Set(Object.keys(d))));
  const welcomed: (() => void)[] = [];
  const session: WorldSession = { ws, player, remotes, entities, myId: null, stop, toggleJoin, whenWelcomed: (cb) => { welcomed.push(cb); } };
  let seq = 0;

  ws.onStatus((s) => {
    $connection.set(s);
    if (s !== "online") { session.myId = null; $presence.set(null); player.setEnabled(false); remotes.clear(); $entities.set({}); $drafts.set({}); $remoteCount.set(0); }
  });

  ws.on((m: ServerMessage) => {
    const now = performance.now();
    switch (m.msg.case) {
      case "welcome": {
        const w = m.msg.value;
        if (w.protocolVersion !== PROTOCOL_VERSION) { location.reload(); return; }
        session.myId = w.playerId;
        remotes.clear();
        $entities.set({});
        if (w.spawn) { player.spawn(w.spawn, w.spawnYaw); entities.recheckTraps(); }
        player.setEnabled($roomPhase.get() === "lobby"); // in the room the room's player walks (spec 2o §4.1)
        void onWelcome();
        for (const cb of welcomed) cb();
        break;
      }
      case "goodbye":
        if (m.msg.value.reason === "REPLACED") { toast("你在另一個分頁登入了，這裡已離線", 8000); ws.close(); }
        if (m.msg.value.reason === "ACCOUNT_DISABLED") { toast("這個帳號已被停用", 8000); ws.close(); $session.set(null); $panel.set("login"); }
        if (m.msg.value.reason === "PROTOCOL_MISMATCH") location.reload();
        break;
      case "snapshot":
        for (const p of m.msg.value.players) if (p.id !== session.myId) remotes.upsert(p, now);
        $remoteCount.set(remotes.count());
        break;
      case "joined":
        if (m.msg.value.player && m.msg.value.player.id !== session.myId) remotes.upsert(m.msg.value.player, now);
        $remoteCount.set(remotes.count());
        break;
      case "left":
        remotes.remove(m.msg.value.id);
        $remoteCount.set(remotes.count());
        break;
      case "correction":
        if (m.msg.value.pos) { player.applyCorrection(m.msg.value.pos); entities.recheckTraps(); }
        if (m.msg.value.reason === "LEAVE_PLOT") toast("已離開領地");
        break;
      case "presence":
        $presence.set(presenceFromProto(m.msg.value));
        break;
      case "plotsChanged":
        void handlePlotsChanged();
        break;
      case "markers":
        void loadMarkers(m.msg.value.plotId);
        break;
      case "mail":
        onMailChanged(m.msg.value.agentId);
        break;
      case "assetsChanged": // an upload, an agent's upload or a transfer (spec 2o §3.6)
        refreshMyAssets().catch((err) => console.error("assets", err));
        break;
      case "chat":
        onChatMessage(m.msg.value);
        break;
      case "chatHistory":
        onChatHistory(m.msg.value);
        break;
      case "chatDeleted":
        onChatDeleted(m.msg.value);
        break;
      case "draft":
        $drafts.set(draftsAfter($drafts.get(), m.msg.value.plotId, m.msg.value.draftVersion)); // before the rows move, so live stays hidden through the gap
        $entities.set(dropDrafts($entities.get(), m.msg.value.plotId));
        break;
      case "entityAdded":
        if (m.msg.value.entity) {
          $drafts.set(draftsSeen($drafts.get(), m.msg.value.entity));
          $entities.setKey(m.msg.value.entity.id, m.msg.value.entity);
        }
        break;
      case "entityRemoved": {
        const next = { ...$entities.get() };
        delete next[m.msg.value.entityId];
        $entities.set(next);
        break;
      }
      case "error":
        onChatError(m.msg.value.code);
        toast(describeError(m.msg.value.code, m.msg.value.message));
        break;
      default:
        break;
    }
  });

  const inputTimer = setInterval(() => {
    if ($connection.get() !== "online" || !session.myId || $roomPhase.get() !== "lobby") return;
    const s = player.state();
    ws.send(client({ case: "input", value: create(ClientInputSchema, { seq: ++seq, pos: { x: s.x, y: s.y, z: s.z }, yaw: s.yaw, pitch: s.pitch, anim: s.anim }) }));
  }, 50);

  const renderObs = scene.onBeforeRenderObservable.add(() => {
    remotes.render(performance.now());
    const s = player.state();
    world.atmosphere.follow(s.x, s.z);
    hud.setCoords(s.x, s.y, s.z, zoneOf(s.x, s.z, world.cfg.zoneSize), plotLine(plotLocal(s.x, s.y, s.z, $plots.get(), world.height)));
    hud.setHeading(s.yaw, s.x, s.z);
    const nowMs = performance.now();
    if (nowMs - lastPosAt > 500) { lastPosAt = nowMs; $playerPos.set({ x: s.x, z: s.z }); }
  });

  const unsubLeave = $leaveRequest.listen(() => {
    ws.send(client({ case: "leavePlot", value: create(LeavePlotSchema, { eject: true }) }));
  });

  function toggleJoin(): void {
    const p = $presence.get();
    if (!p || p.consentMode !== "join") return;
    if (p.state === "joined") ws.send(client({ case: "leavePlot", value: create(LeavePlotSchema, { eject: false }) }));
    else ws.send(client({ case: "joinPlot", value: create(JoinPlotSchema, { plotId: p.plotId }) }));
  }

  function stop(): void {
    unsubLeave();
    clearInterval(inputTimer);
    scene.onBeforeRenderObservable.remove(renderObs);
    ws.close();
    player.setEnabled(false);
  }

  ws.connect();
  return session;
}
